/**
 * The two icon sets a company picks from, and the ten colours they sit on —
 * `docs/marks-and-icons.md` §1.
 *
 * Aaron: "in the division section where you create new divisions, they already
 * select a color — let's have them select an icon." So a division carries a
 * glyph as well as a colour, an equipment **type** carries one (not each
 * machine — a company has one 72-inch Z-turn icon and forty of the machines),
 * and both show up as the mark on every list row, record header and phone card.
 *
 * **A slug is a database value**, so these keys never change once shipped;
 * labels can, because nothing stores them. A glyph that is dropped from a set
 * has to keep its entry or every row holding it would draw nothing.
 *
 * **The shapes are data, not markup.** Each glyph is a list of
 * `{ t: 'path' | 'circle' | 'rect', ... }`, so `Glyph` renders real React
 * elements and no part of this file ever reaches `dangerouslySetInnerHTML`.
 * They were lifted from `design/mockups/37-list-marks/html/marks-04-pickers.html`
 * by a script rather than redrawn, which is why they are one stroke family:
 * 24 viewBox, 1.9 stroke, round caps and joins, no fills — the same rule
 * `frame/icons.tsx` already follows.
 *
 * The spec's heading says the equipment set has thirty; it lists twenty-nine,
 * and twenty-nine is what the picker draws. The thirtieth was never named.
 */
import type { ReactNode } from 'react'
import {
  DIVISION_GLYPH_SLUGS, EQUIPMENT_GLYPH_SLUGS, FALLBACK_COLOUR, FALLBACK_GLYPH,
  MARK_PALETTE, isPaletteColour, type MarkColourName,
} from '@rowted/shared'

/** One shape of one glyph. The attribute names are SVG's, not ours. */
export interface GlyphPart {
  t: 'path' | 'circle' | 'rect' | 'line' | 'ellipse' | 'polyline' | 'polygon'
  [attr: string]: string
}

export interface GlyphEntry {
  /** What the picker calls it. Free to change — nothing stores it. */
  label: string
  parts: GlyphPart[]
}

/**
 * What kind of work it is — the glyph on every job in that division.
 *
 * The six divisions a seed creates map to `leaf`, `flower`, `drop`, `tree`,
 * `bricks` and `snow`; migration 0103 writes those and makes the column NOT
 * NULL, so no job can reach a list with nothing to draw.
 */
export const DIVISION_GLYPHS: Record<string, GlyphEntry> = {
  leaf: { label: "Maintenance", parts: [{"t": "path", "d": "M4 20c0-7 4-12 12-13-1 8-6 12-12 13z"}, {"t": "path", "d": "M4 20l7-7"}] },
  mower: { label: "Mowing", parts: [{"t": "rect", "x": "3", "y": "10", "width": "13", "height": "6", "rx": "1.5"}, {"t": "circle", "cx": "7", "cy": "18", "r": "2.5"}, {"t": "circle", "cx": "17", "cy": "17", "r": "3"}, {"t": "path", "d": "M16 10l3-5h2"}] },
  flower: { label: "Planting", parts: [{"t": "circle", "cx": "12", "cy": "9", "r": "2.5"}, {"t": "path", "d": "M12 3v3.5M12 11.5V21M6.8 6l2.5 2.5M17.2 6l-2.5 2.5M5 9h3.5M15.5 9H19M7 17c2-2 3.5-2 5-0M17 17c-2-2-3.5-2-5 0"}] },
  tree: { label: "Tree care", parts: [{"t": "path", "d": "M12 3l6 8h-3l4 5H5l4-5H6z"}, {"t": "path", "d": "M12 16v5"}] },
  hedge: { label: "Hedges", parts: [{"t": "path", "d": "M4 20V10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10M4 14h16M9 6V3M15 6V3"}] },
  drop: { label: "Irrigation", parts: [{"t": "path", "d": "M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11z"}, {"t": "path", "d": "M9 14a3 3 0 0 0 3 3"}] },
  sprinkler: { label: "Sprinklers", parts: [{"t": "path", "d": "M12 21v-8M8 13h8M12 13l-5-5M12 13l5-5M12 13V5M6 5l1 2M18 5l-1 2"}] },
  bricks: { label: "Hardscape", parts: [{"t": "path", "d": "M3 7h18M3 12h18M3 17h18M8 7v5M16 7v5M12 12v5M3 7v10h18V7"}] },
  fence: { label: "Fencing", parts: [{"t": "path", "d": "M5 21V8l2-3 2 3v13M15 21V8l2-3 2 3v13M3 12h18M3 17h18"}] },
  bulb: { label: "Lighting", parts: [{"t": "path", "d": "M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.8.6 1.5 1.5 1.5 2.1v2h4v-2c0-.6.7-1.5 1.5-2.1A6 6 0 0 0 12 3z"}] },
  snow: { label: "Snow", parts: [{"t": "path", "d": "M12 3v18M3 12h18M5.5 5.5l13 13M18.5 5.5l-13 13"}, {"t": "path", "d": "M12 3l-2 2M12 3l2 2M12 21l-2-2M12 21l2-2M3 12l2-2M3 12l2 2M21 12l-2-2M21 12l-2 2"}] },
  shovel: { label: "Shovelling", parts: [{"t": "path", "d": "M13 3l8 8M9 9l6 6M6 12l-3 3a3 3 0 0 0 0 4l2 2a3 3 0 0 0 4 0l3-3z"}] },
  rake: { label: "Cleanup", parts: [{"t": "path", "d": "M12 3v10M6 13h12M6 13v5M9 13v5M12 13v5M15 13v5M18 13v5"}] },
  grass: { label: "Turf", parts: [{"t": "path", "d": "M3 21c2-6 4-9 6-11M9 21c1-5 3-8 5-10M15 21c1-4 2-6 4-8M12 21c0-3 .5-6 2-9M6 21c0-2 .5-4 1-6"}] },
  bug: { label: "Pest control", parts: [{"t": "circle", "cx": "12", "cy": "13", "r": "5"}, {"t": "path", "d": "M12 8V5M9 5l1.5 2M15 5l-1.5 2M5 13h2M17 13h2M6 18l2-2M18 18l-2-2M6 9l2 2M18 9l-2 2"}] },
  spray: { label: "Fertiliser", parts: [{"t": "path", "d": "M9 8h6v13H9zM10 8V5h4v3M14 5l4-2M18 6l3-1M17 9l3 1"}] },
  excavator: { label: "Excavation", parts: [{"t": "path", "d": "M3 20h12M4 17h10v3M6 17v-5h5l3 5M11 12l4-6h3l-2 6M18 6l2 3-3 1"}, {"t": "circle", "cx": "6", "cy": "20", "r": "1"}, {"t": "circle", "cx": "12", "cy": "20", "r": "1"}] },
  pipe: { label: "Drainage", parts: [{"t": "path", "d": "M3 8h8a4 4 0 0 1 4 4v8M3 12h8M13 20h4"}] },
  wheelbarrow: { label: "General labour", parts: [{"t": "path", "d": "M3 10h13l-2 6H6zM6 16l-3 4M16 10l3-6h2"}, {"t": "circle", "cx": "9", "cy": "19", "r": "2"}] },
  wrench: { label: "Repairs", parts: [{"t": "path", "d": "M14.7 6.3a4 4 0 0 0 5 5L9.4 21.6a2 2 0 0 1-3-3z"}, {"t": "path", "d": "M14.7 6.3L17 4l3 3-2.3 2.3"}] },
  truck: { label: "Hauling", parts: [{"t": "path", "d": "M3 6h11v10H3zM14 10h4l3 3v3h-7z"}, {"t": "circle", "cx": "7", "cy": "18", "r": "2"}, {"t": "circle", "cx": "17", "cy": "18", "r": "2"}] },
  star: { label: "Holiday decor", parts: [{"t": "path", "d": "M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z"}] },
  pool: { label: "Pools", parts: [{"t": "path", "d": "M3 16c2-2 4-2 6 0s4 2 6 0 4-2 6 0M3 20c2-2 4-2 6 0s4 2 6 0 4-2 6 0M8 13V5a2 2 0 0 1 4 0M14 13V5a2 2 0 0 1 4 0"}] },
  broom: { label: "Sweeping", parts: [{"t": "path", "d": "M14 3l-6 10M7 13l-3 7 7-2 3-6z"}] },
}

/**
 * What kind of machine it is — picked once on the equipment **type**.
 *
 * A family suggests one (`EQUIPMENT_FAMILY_DEFAULTS`) and the person can
 * override it: two companies both own skid steers and one of them thinks of
 * them as loaders, so the suggestion is a starting point rather than a rule.
 */
export const EQUIPMENT_GLYPHS: Record<string, GlyphEntry> = {
  pickup: { label: "Pickup", parts: [{"t": "path", "d": "M3 15V9h8l3-4h4v10H3zM14 9h4"}, {"t": "circle", "cx": "7", "cy": "17", "r": "2"}, {"t": "circle", "cx": "17", "cy": "17", "r": "2"}] },
  dump: { label: "Dump truck", parts: [{"t": "path", "d": "M3 15V8h9l2 7H3zM12 8l2-3h4v10M14 15h6"}, {"t": "circle", "cx": "7", "cy": "17", "r": "2"}, {"t": "circle", "cx": "17", "cy": "17", "r": "2"}] },
  van: { label: "Van", parts: [{"t": "path", "d": "M3 15V8h12l6 4v3H3z"}, {"t": "circle", "cx": "7", "cy": "17", "r": "2"}, {"t": "circle", "cx": "17", "cy": "17", "r": "2"}] },
  trailer: { label: "Trailer", parts: [{"t": "path", "d": "M2 15h16V7H2zM18 11h4"}, {"t": "circle", "cx": "7", "cy": "18", "r": "2"}, {"t": "circle", "cx": "13", "cy": "18", "r": "2"}] },
  enclosed: { label: "Enclosed trailer", parts: [{"t": "path", "d": "M2 16h16V6H2zM18 11h4M2 10h16"}, {"t": "circle", "cx": "8", "cy": "19", "r": "2"}] },
  dumptrailer: { label: "Dump trailer", parts: [{"t": "path", "d": "M2 15h16l-2-8H4zM18 11h4"}, {"t": "circle", "cx": "7", "cy": "18", "r": "2"}, {"t": "circle", "cx": "13", "cy": "18", "r": "2"}] },
  zturn: { label: "Z-turn mower", parts: [{"t": "path", "d": "M5 12h9l3-6h3M6 12V9h5v3M3 16h14"}, {"t": "circle", "cx": "6", "cy": "18", "r": "2.5"}, {"t": "circle", "cx": "16", "cy": "17", "r": "3.5"}] },
  walkbehind: { label: "Walk-behind mower", parts: [{"t": "rect", "x": "3", "y": "11", "width": "11", "height": "5", "rx": "1.5"}, {"t": "path", "d": "M14 13l5-8h2"}, {"t": "circle", "cx": "6", "cy": "18", "r": "2"}, {"t": "circle", "cx": "12", "cy": "18", "r": "2"}] },
  standon: { label: "Stand-on mower", parts: [{"t": "path", "d": "M4 13h10V8H4zM14 13l3-7h2M14 10v8"}, {"t": "circle", "cx": "6", "cy": "17", "r": "2.5"}, {"t": "circle", "cx": "13", "cy": "18", "r": "2"}] },
  trimmer: { label: "String trimmer", parts: [{"t": "path", "d": "M5 20l10-10M15 10l4-4M13 8l3 3"}, {"t": "circle", "cx": "6", "cy": "19", "r": "3"}] },
  hedgetrimmer: { label: "Hedge trimmer", parts: [{"t": "path", "d": "M3 15h12M4 12h10M5 18h8M15 13l5-6M18 10l2 2"}] },
  chainsaw: { label: "Chainsaw", parts: [{"t": "path", "d": "M3 13h5v4H3zM8 14h12v3H8zM8 14l2-4h6M5 13V9h3"}] },
  blower: { label: "Blower", parts: [{"t": "path", "d": "M4 8h8v8H4zM12 10l8-2v8l-8-2M8 16v4"}] },
  backpack: { label: "Backpack blower", parts: [{"t": "path", "d": "M7 5h6v12H7zM13 8h5v6h-5M9 17v4h2v-4M10 5V3"}] },
  edger: { label: "Edger", parts: [{"t": "path", "d": "M4 19l8-8M12 11l5-5M15 6l2 2"}, {"t": "circle", "cx": "6", "cy": "19", "r": "2.5"}, {"t": "path", "d": "M6 19h3"}] },
  aerator: { label: "Aerator", parts: [{"t": "rect", "x": "3", "y": "10", "width": "12", "height": "5", "rx": "1"}, {"t": "path", "d": "M5 15v3M8 15v3M11 15v3M14 15v3M15 12l4-6h2"}] },
  skid: { label: "Skid steer", parts: [{"t": "path", "d": "M4 14h9V8H4zM13 11h4l3 3v2H4M9 8V5h3"}, {"t": "circle", "cx": "7", "cy": "18", "r": "2"}, {"t": "circle", "cx": "16", "cy": "18", "r": "2"}] },
  miniex: { label: "Mini excavator", parts: [{"t": "path", "d": "M3 20h12M4 17h10v3M6 17v-5h5l3 5M11 12l4-6h3l-2 6M18 6l2 3-3 1"}, {"t": "circle", "cx": "6", "cy": "20", "r": "1"}, {"t": "circle", "cx": "12", "cy": "20", "r": "1"}] },
  tractor: { label: "Tractor", parts: [{"t": "path", "d": "M5 12h7V7H5zM12 12h5l2 4H3M8 7V4h3"}, {"t": "circle", "cx": "6", "cy": "18", "r": "3"}, {"t": "circle", "cx": "17", "cy": "18", "r": "2"}] },
  plow: { label: "Plow", parts: [{"t": "path", "d": "M3 16h12l2-3H6zM8 13V8h7l3 5M3 8h3"}, {"t": "circle", "cx": "7", "cy": "18", "r": "2"}, {"t": "circle", "cx": "15", "cy": "18", "r": "2"}] },
  spreader: { label: "Spreader", parts: [{"t": "path", "d": "M7 5h10l-2 8H9zM12 13v3M6 20l6-4 6 4"}] },
  generator: { label: "Generator", parts: [{"t": "rect", "x": "3", "y": "9", "width": "18", "height": "10", "rx": "2"}, {"t": "path", "d": "M7 9V6h10v3M8 14h3l1-2 2 4 1-2h2"}] },
  washer: { label: "Pressure washer", parts: [{"t": "rect", "x": "3", "y": "11", "width": "12", "height": "7", "rx": "2"}, {"t": "path", "d": "M6 11V7h6v4M15 14h3l3-3"}, {"t": "circle", "cx": "6", "cy": "20", "r": "1.5"}, {"t": "circle", "cx": "12", "cy": "20", "r": "1.5"}] },
  sprayer: { label: "Sprayer", parts: [{"t": "rect", "x": "4", "y": "9", "width": "12", "height": "9", "rx": "2"}, {"t": "path", "d": "M8 9V6h4v3M16 12h3l2 2"}, {"t": "circle", "cx": "7", "cy": "20", "r": "1.5"}, {"t": "circle", "cx": "13", "cy": "20", "r": "1.5"}] },
  chipper: { label: "Chipper", parts: [{"t": "path", "d": "M4 16h10l3-5H7zM14 16l5 2M7 11V7h5"}, {"t": "circle", "cx": "6", "cy": "19", "r": "2"}, {"t": "circle", "cx": "12", "cy": "19", "r": "2"}] },
  plate: { label: "Plate compactor", parts: [{"t": "path", "d": "M5 15h14l-2 4H7zM8 15V9h8v6M12 9V5"}] },
  auger: { label: "Auger", parts: [{"t": "path", "d": "M12 3v18M8 7c3 2 5 2 8 0M8 11c3 2 5 2 8 0M8 15c3 2 5 2 8 0"}] },
  ladder: { label: "Ladder", parts: [{"t": "path", "d": "M7 3v18M17 3v18M7 7h10M7 12h10M7 17h10"}] },
  toolbox: { label: "Other", parts: [{"t": "rect", "x": "3", "y": "9", "width": "18", "height": "11", "rx": "2"}, {"t": "path", "d": "M8 9V6h8v3M3 14h18"}] },
}

export type GlyphSet = 'division' | 'equipment'

export const GLYPH_SETS: Record<GlyphSet, Record<string, GlyphEntry>> = {
  division: DIVISION_GLYPHS,
  equipment: EQUIPMENT_GLYPHS,
}

/**
 * Is this a slug the set has? The server asks the same question of
 * `shared/src/marks.ts`, and these tables are built from those lists, so the
 * two can only ever agree.
 */
export const isGlyph = (set: GlyphSet, slug: string | null | undefined): boolean =>
  !!slug && Object.hasOwn(GLYPH_SETS[set], slug)

/* The slug lists shared exposes must be exactly what this file draws; a glyph
   the picker offers and the renderer has never heard of is a blank tile. */
const SLUG_LISTS: Record<GlyphSet, readonly string[]> = {
  division: DIVISION_GLYPH_SLUGS,
  equipment: EQUIPMENT_GLYPH_SLUGS,
}
export const glyphSlugs = (set: GlyphSet): readonly string[] => SLUG_LISTS[set]

/**
 * A colour as CSS, whether it arrived as a palette name or as the hex a
 * division has stored since migration 0026.
 *
 * Both are allowed on the way in because `divisions.colour` is a hex column and
 * predates the palette; the picker only ever writes one of the ten, so the hex
 * branch is for rows written before it existed.
 */
export const markInk = (colour: string | null | undefined): string => {
  if (!colour) return FALLBACK_COLOUR
  if (isPaletteColour(colour)) return colour
  const named = MARK_PALETTE[colour as MarkColourName]
  if (named) return named
  /* A hex from before the palette existed (`divisions.colour` since 0026). */
  return /^#[0-9a-fA-F]{3,8}$/.test(colour) ? colour : FALLBACK_COLOUR
}

/**
 * The tint behind the glyph: the ink at 12% over paper.
 *
 * `color-mix` rather than a second stored value, so a colour and its tint can
 * never drift apart, and so dark mode gets the right answer from the theme's
 * own `--surface` rather than from a table of doubled constants.
 */
export const markTint = (colour: string | null | undefined): string =>
  /* `rgb(var(--color-surface))`, not `var(--surface)`: the tokens are stored as
     bare `R G B` triples so they can be used at any opacity, and a triple is
     not a colour — `color-mix` silently gave up on it and every tile drew with
     no tint at all. Found by looking at the Jobs board rather than by reading
     the diff, which is the only way this kind of mistake ever surfaces. */
  `color-mix(in srgb, ${markInk(colour)} 12%, rgb(var(--color-surface)))`

export interface GlyphProps {
  set: GlyphSet
  slug: string | null | undefined
  /** The drawn size in px. The viewBox is always 24. */
  size?: number
  colour?: string
  strokeWidth?: number
}

/**
 * One glyph, drawn.
 *
 * `vectorEffect` is deliberately absent: these are drawn at 13–32 px and a
 * stroke that refuses to scale looks heavy at 32 and spindly at 13. The stroke
 * scales with the tile, which is what the three sizes on board 2 show.
 */
export function Glyph({
  set, slug, size = 16, colour = 'currentColor', strokeWidth = 1.9,
}: GlyphProps): ReactNode {
  const entry = GLYPH_SETS[set][slug ?? ''] ?? GLYPH_SETS[set][FALLBACK_GLYPH]
    ?? DIVISION_GLYPHS[FALLBACK_GLYPH]!
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke={colour} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
      {entry.parts.map((part, index) => {
        const { t, ...attrs } = part
        const key = `${t}-${index}`
        if (t === 'circle') return <circle key={key} {...attrs} />
        if (t === 'rect') return <rect key={key} {...attrs} />
        if (t === 'line') return <line key={key} {...attrs} />
        if (t === 'ellipse') return <ellipse key={key} {...attrs} />
        if (t === 'polyline') return <polyline key={key} {...attrs} />
        if (t === 'polygon') return <polygon key={key} {...attrs} />
        return <path key={key} {...attrs} />
      })}
    </svg>
  )
}

/** What a glyph is called, for a title attribute or a picker tile. */
export const glyphLabel = (set: GlyphSet, slug: string | null | undefined): string =>
  GLYPH_SETS[set][slug ?? '']?.label ?? 'Other'
